import { IconButton, Stack, Typography } from "@mui/material";
import { CustomCellRendererProps } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import { globalTheme } from "../../styles";
import { AusPostIcon, DeleteIcon, InhouseParcelIcon, PrintIcon } from "../Icon";
import {
  moveShipmentItems,
  ShipmentsDeleteConfirmationDrawer,
} from "./ShipmentsDeleteConfirmationDrawer/ShipmentsDeleteConfirmationDrawer";
import { usePrinter, useUpdateAction } from "../../hooks";
import { useDeleteShipment } from "../../hooks/useDeleteShipments";
import { IDeleteShipments } from "../../interfaces/IDeleteShipment";
import { AUTHENTICATED_EMPLOYEE_JWT } from "../../constants";

interface ConsignmentCellProps extends CustomCellRendererProps {
  refreshData: () => void;
  setSnackBar: (snackBar: { open: boolean; message: string; severity: string }) => void;
}

// Memoise cell level component to avoid unnecessaru re-renders. This helps enhancing performance improvements
export const ConsignmentCell = React.memo((params: ConsignmentCellProps) => {
  const { data, refreshData, setSnackBar } = params;
  const [actionUpdateRequest, setActionUpdateRequest] = useState({ requestAvailable: false, request: [] });

  const [openDeleteConfirmationShipmentsDrawer, setDeleteConfirmationShipmentsDrawerOpen] = useState(false);
  const [shipmentDetailItem, setShipmentDetailItem] = useState({
    shipmentOrderId: "",
    shipmentCustomerName: "",
    shipmentId: "",
    shipmentItemTrackingId: "",
    shipmentCarrier: "",
  });

  if (!data.shipmentDetails || !Array.isArray(data.shipmentDetails)) {
    return null; // Or handle this case as needed
  }

  const { shipmentDetails } = data;

  const getCarrierImage = (courierName) => {
    if (courierName === "EPARCEL") {
      return <AusPostIcon width="25" height="25" />;
    }
    if (courierName === "MYFASTWAY") {
      return (
        <img src="https://vtlogo.com/wp-content/uploads/2021/02/fastway-couriers-vector-logo.png" width="30px" height="24px" />
      );
    }
    if (courierName === "DIRECTFREIGHT") {
      return <img src="https://www.directfreight.com.au/images/logo.gif" width="30px" height="24px" />;
    }
  };

  const deleteShipment: IDeleteShipments = {
    orderId: String(shipmentDetailItem.shipmentOrderId),
    shipmentId: shipmentDetailItem.shipmentId,
    shippingCarrier: shipmentDetailItem.shipmentCarrier,
  };

  const [deleteEnableShipment, setDeleteEnableShipment] = useState<boolean>(false);

  const {
    loadingDeleteShipment,
    fetchingDeleteShipment,
    errorFetchingDeleteShipment,
    deleteShipmentResponse,
    fetchDeleteShipmentError,
    refetchDeleteShipment,
  } = useDeleteShipment({ enabled: deleteEnableShipment, deleteShipmentData: deleteShipment });

  //TODO: The API of delete Shipments Should be integrated in below handle

  const handleShipmentsDeleteClick = (item) => {
    setShipmentDetailItem({
      shipmentCarrier: item?.shippingCarrier,
      shipmentItemTrackingId: item?.shippedItems?.map((item) => item.itemTrackingId).join(", "),
      shipmentId: item?.shipmentId,
      shipmentCustomerName: item?.name,
      shipmentOrderId: item?.orderIds,
    });
  };

  const handleShipmentPrint = (pdfLink, courierName) => {
    if (pdfLink && courierName) {
      usePrinter(pdfLink, courierName);
    }
  };

  const handleShipmentsDeleteConfirm = () => {
    setDeleteEnableShipment(true);
    //setDeleteConfirmationShipmentsDrawerOpen(false);
  };

  const handleShipmentsDeleteDrawerClose = () => {
    setDeleteConfirmationShipmentsDrawerOpen(false);
  };

  const prepareActionUpdateRequest = (deleteShipment) => {
    const shipmentItem = moveShipmentItems.find((item) => item.value === shipmentStatus);
    return {
      actionType: shipmentItem?.label,
      idList: [deleteShipment?.orderId],
      changes: [{ fieldName: "shippingStatus", oldValue: "", newValue: shipmentStatus }],
    };
  };

  const { successfullUpdatedAction, updateActionResponseData } = useUpdateAction({
    updateActionRequest: actionUpdateRequest.request,
    enabled: actionUpdateRequest.requestAvailable,
  });

  useEffect(() => {
    if (updateActionResponseData?.code === "UNAUTHORIZED") {
      localStorage.removeItem(AUTHENTICATED_EMPLOYEE_JWT);
      window.location.reload();
    }
  }, [updateActionResponseData]);

  useEffect(() => {
    if (shipmentDetailItem && shipmentDetailItem.shipmentId) {
      setDeleteConfirmationShipmentsDrawerOpen(true);
    }
  }, [shipmentDetailItem]);

  useEffect(() => {
    if (deleteShipmentResponse && shipmentStatus) {
      setActionUpdateRequest({ requestAvailable: true, request: [prepareActionUpdateRequest(deleteShipment)] });
    }
  }, [deleteShipmentResponse]);

  useEffect(() => {
    if (successfullUpdatedAction && shipmentStatus) {
      setSnackBar({
        message: "Shipment is successfully deleted and Order is moved to " + shipmentStatus,
        severity: "success",
        open: true,
      });
      handleShipmentsDeleteDrawerClose();
      refreshData();
    }
  }, [successfullUpdatedAction]);

  useEffect(() => {
    if (fetchDeleteShipmentError) {
      setSnackBar({
        message: "Failed to delete shipment",
        severity: "error",
        open: true,
      });
    }
  }, [fetchDeleteShipmentError]);

  const [shipmentStatus, setShipmentStatus] = useState();

  const handleSelectedStatus = (value) => {
    setShipmentStatus(value);
  };

  return (
    <Stack>
      {shipmentDetails.map((item, key) => (
        <Stack key={key} direction={"row"} spacing={2} justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <>{getCarrierImage(item.courierName)}</>
            <Stack>
              {!getCarrierImage(item.courierName) ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">Carrier:</Typography>
                  <Typography variant="body2" color={globalTheme.primaryColor}>
                    {item.courierName}
                  </Typography>
                </Stack>
              ) : undefined}
              {item.ShippingCost && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">Shipping cost:</Typography>
                  <Typography variant="body2" color={globalTheme.primaryColor}>
                    {item.ShippingCost}
                  </Typography>
                </Stack>
              )}
              {item.shippedItems && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">Tracking number:</Typography>
                  <Typography variant="body2" color={globalTheme.primaryColor}>
                    {item?.shippedItems?.map((item) => item.itemTrackingId).join(", ")}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => handleShipmentPrint(item.pdfLink, item.shippingCarrier)}>
              <PrintIcon />
            </IconButton>
            {item?.deleteEnable && item.courierName != "Amazon WI" && (
              <IconButton onClick={() => handleShipmentsDeleteClick(item)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
      ))}
      <ShipmentsDeleteConfirmationDrawer
        open={openDeleteConfirmationShipmentsDrawer}
        onClose={handleShipmentsDeleteDrawerClose}
        onConfirm={handleShipmentsDeleteConfirm}
        orderID={shipmentDetailItem.shipmentOrderId}
        shipmentID={shipmentDetailItem.shipmentId}
        itemTrackingID={shipmentDetailItem.shipmentItemTrackingId}
        customerName={shipmentDetailItem.shipmentCustomerName}
        onSelectedStatus={handleSelectedStatus}
      />
    </Stack>
  );
});
